import { useRouteState } from '../routeState'
import { useRefreshGuard } from '../refreshGuards'
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react'
import {
  AlertTriangle,
  ArrowRight,
  BookOpenText,
  Clock3,
  Cloud,
  CloudOff,
  Download,
  FilePlus2,
  LogOut,
  Moon,
  MoreHorizontal,
  Plus,
  Search,
  Sprout,
  Sun,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import {
  MAX_PROJECT_FILE_BYTES,
  downloadFullProjectFile,
  parseFullProjectFile,
} from '../projectTransfer'
import type { ProjectSummary, StoryProject, SyncState, Theme } from '../types'
import { useTasks } from '../TaskProvider'

type ProjectDashboardProps = {
  projects: ProjectSummary[] | null
  activeProjectId: string | null
  syncState: SyncState
  error: string | null
  theme: Theme
  onThemeChange: (theme: Theme) => void
  onOpenProject: (projectId: string) => Promise<void>
  onCreateProject: (title: string) => Promise<void>
  onImportProject: (project: StoryProject) => Promise<void>
  onExportProject: (projectId: string) => Promise<StoryProject>
  onDeleteProject: (projectId: string) => Promise<void>
  onLogout: () => Promise<void>
}
function formatUpdated(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Recently edited'
  const elapsed = Date.now() - date.getTime()
  const minutes = Math.floor(elapsed / 60_000)
  if (minutes < 1) return 'Edited just now'
  if (minutes < 60) return 'Edited ' + minutes + 'm ago'
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return 'Edited ' + hours + 'h ago'
  const days = Math.floor(hours / 24)
  if (days < 7) return 'Edited ' + days + 'd ago'
  return (
    'Edited ' +
    new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year:
        date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
    }).format(date)
  )
}

function ProjectActions({ project, disabled, onDelete }: {
  project: ProjectSummary
  disabled: boolean
  onDelete: () => void
}) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const wrapper = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const action = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!open) return
    action.current?.focus()
    const dismiss = (event: PointerEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      setOpen(false)
      trigger.current?.focus()
    }
    document.addEventListener('pointerdown', dismiss)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('pointerdown', dismiss)
      document.removeEventListener('keydown', escape)
    }
  }, [open])
  return <div className="project-menu" ref={wrapper} onBlur={event => {
    // Safari may blur a button to no element on mousedown, before its click.
    // Close for a real focus destination; outside pointer presses close above.
    if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) setOpen(false)
  }} onKeyDown={event => {
    if (event.key === 'Tab') {
      // Let the browser choose the next focus target before removing the menu.
      window.setTimeout(() => setOpen(false), 0)
      return
    }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault()
      if (open) action.current?.focus()
      else setOpen(true)
    }
  }}>
    <button ref={trigger} type="button" className="icon-button" disabled={disabled}
      aria-label={'Actions for ' + project.title} title="Project actions"
      aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined}
      onClick={() => setOpen(value => !value)}><MoreHorizontal size={17} /></button>
    {open && <div id={id} className="project-menu-panel" role="menu" aria-label={'Actions for ' + project.title}>
      <button ref={action} type="button" role="menuitem" className="danger-action" disabled={disabled}
        onClick={() => {
          trigger.current?.focus()
          setOpen(false)
          onDelete()
        }}><Trash2 size={15} />Delete project</button>
    </div>}
  </div>
}

export function ProjectDashboard({
  projects,
  activeProjectId,
  syncState,
  error,
  theme,
  onThemeChange,
  onOpenProject,
  onCreateProject,
  onImportProject,
  onExportProject,
  onDeleteProject,
  onLogout,
}: ProjectDashboardProps) {
  const { user } = useTasks()
  const [search, setSearch] = useState('')
  const [newProjectOpen, setNewProjectOpen] = useRouteState<boolean>('new-project', false)
  const [newProjectTitle, setNewProjectTitle] = useState('')
  const [deleteId, setDeleteId] = useRouteState<string | null>('trash-project', null)
  const deleteCandidate = projects?.find(project => project.id === deleteId) || null
  const setDeleteCandidate = (project: ProjectSummary | null) => setDeleteId(project?.id || null)
  const [busy, setBusy] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  useRefreshGuard(!!busy || newProjectOpen, 'Finish your project change before refreshing.')
  const newProjectDialogRef = useRef<HTMLFormElement>(null)
  const deleteDialogRef = useRef<HTMLElement>(null)
  const importInputRef = useRef<HTMLInputElement>(null)
  const busyRef = useRef(busy)
  busyRef.current = busy
  const openModal = newProjectOpen
    ? 'create'
    : deleteCandidate
      ? 'delete'
      : null

  useEffect(() => {
    if (!openModal) return
    const previousFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
    const dialog =
      openModal === 'create'
        ? newProjectDialogRef.current
        : deleteDialogRef.current
    const animationFrame = window.requestAnimationFrame(() => {
      const initialFocus = dialog?.querySelector<HTMLElement>(
        '[data-modal-initial-focus]',
      )
      if (dialog && !dialog.contains(document.activeElement))
        initialFocus?.focus()
    })

    const handleKeyDown = (event: KeyboardEvent) => {
      const currentDialog =
        openModal === 'create'
          ? newProjectDialogRef.current
          : deleteDialogRef.current
      if (!currentDialog) return

      if (event.key === 'Escape') {
        if (busyRef.current) return
        event.preventDefault()
        if (openModal === 'create') setNewProjectOpen(false)
        else setDeleteCandidate(null)
        return
      }

      if (event.key !== 'Tab') return
      const focusable = [
        ...currentDialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((element) => element.offsetParent !== null)
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable.at(-1) ?? first
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      } else if (!currentDialog.contains(document.activeElement)) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      window.cancelAnimationFrame(animationFrame)
      document.removeEventListener('keydown', handleKeyDown)
      window.requestAnimationFrame(() => {
        if (previousFocus?.isConnected) previousFocus.focus()
      })
    }
  }, [openModal])

  const filteredProjects = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!projects || !term) return projects ?? []
    return projects.filter((project) =>
      project.title.toLowerCase().includes(term),
    )
  }, [projects, search])

  const totals = useMemo(
    () => ({
      scenes: (projects ?? []).reduce(
        (sum, project) => sum + project.sceneCount,
        0,
      ),
      words: (projects ?? []).reduce(
        (sum, project) => sum + project.wordCount,
        0,
      ),
    }),
    [projects],
  )

  const submitNewProject = async (event: FormEvent) => {
    event.preventDefault()
    const title = newProjectTitle.trim() || 'Untitled Story'
    setBusy('create')
    setActionError(null)
    try {
      await onCreateProject(title)
      setNewProjectOpen(false)
      setNewProjectTitle('')
    } catch (caught) {
      setActionError(
        caught instanceof Error
          ? caught.message
          : 'The project could not be created.',
      )
    } finally {
      setBusy(null)
    }
  }

  const openProject = async (projectId: string) => {
    setBusy('open:' + projectId)
    setActionError(null)
    try {
      await onOpenProject(projectId)
    } catch (caught) {
      setActionError(
        caught instanceof Error
          ? caught.message
          : 'The project could not be opened.',
      )
    } finally {
      setBusy(null)
    }
  }

  const importProject = async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget
    const file = input.files?.[0]
    input.value = ''
    if (!file) return

    setBusy('import')
    setActionError(null)
    try {
      if (file.size > MAX_PROJECT_FILE_BYTES) {
        throw new Error(
          'That project file is larger than the 160 MB import limit, including audio.',
        )
      }
      const projectToImport = await parseFullProjectFile(await file.text(), user.id)
      await onImportProject(projectToImport)
    } catch (caught) {
      setActionError(
        caught instanceof Error
          ? caught.message
          : 'The project could not be imported.',
      )
    } finally {
      setBusy(null)
    }
  }

  const exportProject = async (project: ProjectSummary) => {
    setBusy('export:' + project.id)
    setActionError(null)
    try {
      await downloadFullProjectFile(await onExportProject(project.id), user.id)
    } catch (caught) {
      setActionError(
        caught instanceof Error
          ? caught.message
          : 'The project could not be downloaded.',
      )
    } finally {
      setBusy(null)
    }
  }

  const deleteProject = async () => {
    if (!deleteCandidate) return
    setBusy('delete:' + deleteCandidate.id)
    setActionError(null)
    try {
      await onDeleteProject(deleteCandidate.id)
      setDeleteCandidate(null)
    } catch (caught) {
      setActionError(
        caught instanceof Error
          ? caught.message
          : 'The project could not be deleted.',
      )
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="project-dashboard">
      <header className="dashboard-header">
        <div className="dashboard-brand">
          <span className="brand-mark">
            <Sprout size={18} />
          </span>
          <div>
            <strong>BRAMBLE</strong>
            <small>STORY LIBRARY</small>
          </div>
        </div>
        <nav>
          <span data-room-tools />
          <span
            className={
              'dashboard-connection dashboard-connection--' + syncState
            }
          >
            {syncState === 'offline' || syncState === 'error' ? (
              <CloudOff size={14} />
            ) : (
              <Cloud size={14} />
            )}
            {syncState === 'loading'
              ? 'Opening library'
              : syncState === 'offline'
                ? 'Offline'
                : syncState === 'error'
                  ? 'Save needs attention'
                  : 'Server library'}
          </span>
          <button
            type="button"
            className="icon-button"
            onClick={() => onThemeChange(theme === 'dark' ? 'light' : 'dark')}
            title={
              'Switch to ' + (theme === 'dark' ? 'light' : 'dark') + ' theme'
            }
          >
            {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <button
            type="button"
            className="icon-button"
            onClick={() => void onLogout()}
            title="Log out"
          >
            <LogOut size={16} />
          </button>
        </nav>
      </header>

      <main className="dashboard-main">
        <section className="dashboard-intro">
          <div>
            <span>YOUR WRITING ROOM</span>
            <h1>
              Every story has
              <br />
              room to branch.
            </h1>
            <p>
              Pick up where you left off, or begin something new. Draft your
              scenes, explore every choice, and keep your story’s world close.
            </p>
          </div>
          <div className="dashboard-totals" aria-label="Library totals">
            <div>
              <strong>{projects?.length ?? 0}</strong>
              <span>projects</span>
            </div>
            <div>
              <strong>{totals.scenes}</strong>
              <span>scenes</span>
            </div>
            <div>
              <strong>{totals.words.toLocaleString()}</strong>
              <span>words</span>
            </div>
          </div>
        </section>

        {(error || actionError) && (
          <div className="dashboard-alert" role="alert">
            {actionError ? <AlertTriangle size={16} /> : <CloudOff size={16} />}
            <span>{actionError || error}</span>
          </div>
        )}

        <section
          className="project-library"
          aria-labelledby="project-library-title"
        >
          <header>
            <div>
              <span>PROJECTS</span>
              <h2 id="project-library-title">Story shelf</h2>
            </div>
            <div className="project-library__actions">
              <label className="dashboard-search">
                <Search size={15} />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Find a story…"
                  aria-label="Find a story"
                />
              </label>
              <input
                ref={importInputRef}
                hidden
                type="file"
                accept=".json,application/json"
                onChange={(event) => void importProject(event)}
              />
              <button
                type="button"
                className="button dashboard-transfer-button"
                onClick={() => {
                  setActionError(null)
                  if (importInputRef.current) {
                    importInputRef.current.value = ''
                    importInputRef.current.click()
                  }
                }}
                disabled={
                  Boolean(busy) ||
                  syncState === 'offline' ||
                  syncState === 'loading'
                }
              >
                <Upload size={15} />{' '}
                {busy === 'import' ? 'Importing…' : 'Import JSON'}
              </button>
              <button
                type="button"
                className="button button--primary dashboard-new-button"
                onClick={() => {
                  setActionError(null)
                  setNewProjectOpen(true)
                }}
                disabled={
                  Boolean(busy) ||
                  syncState === 'offline' ||
                  syncState === 'loading'
                }
              >
                <Plus size={16} /> New project
              </button>
            </div>
          </header>

          {projects === null ? (
            <div className="dashboard-loading">
              <Sprout size={24} />
              <span>Opening the story shelf…</span>
            </div>
          ) : filteredProjects.length > 0 ? (
            <div className="project-grid">
              {filteredProjects.map((project, index) => {
                const isOpening = busy === 'open:' + project.id
                const isExporting = busy === 'export:' + project.id
                return (
                  <article
                    className={`project-card${project.recoveryOnly ? ' project-card--recovery' : ''}`}
                    key={project.id}
                  >
                    <header>
                      <span className="project-card__number">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="project-card__type">
                        <i />{' '}
                        {project.recoveryOnly
                          ? 'BROWSER RECOVERY'
                          : 'BRANCHING STORY'}
                      </span>
                      {activeProjectId === project.id && (
                        <span className="project-card__active">
                          LAST OPENED
                        </span>
                      )}
                      <ProjectActions project={project} disabled={Boolean(busy)} onDelete={() => {
                        setActionError(null)
                        setDeleteCandidate(project)
                      }} />
                    </header>
                    <div className="project-card__body">
                      <BookOpenText size={20} />
                      <h3>{project.title || 'Untitled Story'}</h3>
                      <p>
                        {project.sceneCount}{' '}
                        {project.sceneCount === 1 ? 'scene' : 'scenes'} ·{' '}
                        {project.wordCount.toLocaleString()} words
                      </p>
                    </div>
                    <footer>
                      <span>
                        <Clock3 size={13} /> {formatUpdated(project.updatedAt)}
                      </span>
                      <div>
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => void exportProject(project)}
                          disabled={Boolean(busy)}
                          title={
                            (isExporting ? 'Downloading ' : 'Download ') +
                            project.title +
                            ' as JSON'
                          }
                          aria-label={
                            (isExporting ? 'Downloading ' : 'Download ') +
                            project.title +
                            ' as JSON'
                          }
                        >
                          <Download size={15} />
                        </button>
                        <button
                          type="button"
                          className="button project-open-button"
                          onClick={() => void openProject(project.id)}
                          disabled={Boolean(busy)}
                        >
                          {isOpening
                            ? 'Opening…'
                            : project.recoveryOnly
                              ? 'Recover'
                              : activeProjectId === project.id
                                ? 'Continue'
                                : 'Write'}{' '}
                          <ArrowRight size={14} />
                        </button>
                      </div>
                    </footer>
                  </article>
                )
              })}
            </div>
          ) : projects.length === 0 ? (
            <div className="dashboard-empty">
              <FilePlus2 size={28} />
              <h3>Your shelf is empty.</h3>
              <p>
                Create the first story project. It begins with one blank opening
                scene.
              </p>
              <button
                type="button"
                className="button button--primary"
                onClick={() => setNewProjectOpen(true)}
                disabled={syncState === 'offline'}
              >
                <Plus size={16} /> Create a project
              </button>
            </div>
          ) : (
            <div className="dashboard-empty dashboard-empty--search">
              <Search size={24} />
              <h3>No story matches “{search}”.</h3>
              <button
                type="button"
                className="button"
                onClick={() => setSearch('')}
              >
                Clear search
              </button>
            </div>
          )}
        </section>
      </main>

      <footer className="dashboard-footer">
        <span>BRAMBLE / WRITING STUDIO</span>
        <span data-project-trash />
        <span>
          {syncState === 'offline' || syncState === 'error'
            ? 'Open a project for save and recovery details'
            : 'Autosave to your library'}
        </span>
      </footer>

      {newProjectOpen && (
        <div
          className="dashboard-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !busy)
              setNewProjectOpen(false)
          }}
        >
          <form
            ref={newProjectDialogRef}
            className="dashboard-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-project-title"
            onSubmit={submitNewProject}
          >
            <header>
              <div>
                <span>NEW PROJECT</span>
                <h2 id="new-project-title">Start a new story</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => setNewProjectOpen(false)}
                disabled={Boolean(busy)}
                aria-label="Close"
              >
                <X size={17} />
              </button>
            </header>
            <label>
              <span>PROJECT TITLE</span>
              <input
                autoFocus
                data-modal-initial-focus
                maxLength={120}
                value={newProjectTitle}
                onChange={(event) => setNewProjectTitle(event.target.value)}
                placeholder="My branching story"
              />
            </label>
            <p>
              We’ll start it with one blank opening scene. You can rename
              everything later.
            </p>
            {actionError && (
              <div className="modal-error" role="alert">
                {actionError}
              </div>
            )}
            <footer>
              <button
                type="button"
                className="button"
                onClick={() => setNewProjectOpen(false)}
                disabled={Boolean(busy)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button button--primary"
                disabled={Boolean(busy)}
              >
                <Plus size={15} />{' '}
                {busy === 'create' ? 'Creating…' : 'Create project'}
              </button>
            </footer>
          </form>
        </div>
      )}

      {deleteCandidate && (
        <div
          className="dashboard-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !busy)
              setDeleteCandidate(null)
          }}
        >
          <section
            ref={deleteDialogRef}
            className="dashboard-modal dashboard-modal--danger"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-project-title"
          >
            <header>
              <div>
                <span>MOVE TO TRASH</span>
                <h2 id="delete-project-title">
                  Remove “{deleteCandidate.title}”?
                </h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => setDeleteCandidate(null)}
                disabled={Boolean(busy)}
                aria-label="Close"
              >
                <X size={17} />
              </button>
            </header>
            <p>
              {deleteCandidate.recoveryOnly
                ? 'This permanently discards the only browser recovery copy. No server copy currently exists.'
                : 'This moves the project and its saved drafts to Trash. Either writer can restore it. Permanent deletion requires approval from both silacel and spunkySarb.'}
            </p>
            {actionError && (
              <div className="modal-error" role="alert">
                {actionError}
              </div>
            )}
            <footer>
              <button
                type="button"
                className="button"
                data-modal-initial-focus
                onClick={() => setDeleteCandidate(null)}
                disabled={Boolean(busy)}
              >
                Keep project
              </button>
              <button
                type="button"
                className="button button--danger"
                onClick={() => void deleteProject()}
                disabled={Boolean(busy)}
              >
                <Trash2 size={15} />{' '}
                {busy?.startsWith('delete:')
                  ? 'Moving…'
                  : deleteCandidate.recoveryOnly
                    ? 'Discard recovery'
                    : 'Move to Trash'}
              </button>
            </footer>
          </section>
        </div>
      )}
    </div>
  )
}
